import { Component, OnInit } from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { BalanceSheetService } from '@services/balance-sheet/balance-sheet.service';
import { BehaviorSubject } from 'rxjs';
import { BalanceSheet } from '@models/balance-sheet';
import { AccountTreeNode } from '@models/account-tree-node';
import { MenuService } from '@services/menu/menu.service';
import '../array.extension';

@Component({
  selector: 'app-account-tree',
  templateUrl: './account-tree.component.html',
  styleUrls: ['./account-tree.component.scss']
})
export class AccountTreeComponent implements OnInit {
  /*
      Component
      properties
  */
  bsData: BalanceSheet[];
  treeData$ = new BehaviorSubject<AccountTreeNode[]>([]);
  public nestedDataSource: MatTreeNestedDataSource<AccountTreeNode> = new MatTreeNestedDataSource();
  public nestedTreeControl: NestedTreeControl<AccountTreeNode>;
  // private selectedItem$ = new BehaviorSubject(null);
  public wordingForOrphans = 'Unmatched accounts';
  public errorsDisplayingTheTree = false;
  // private isFirstLoad = true;
  // searchCriteria$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  // get selectedItem() {
  //   return this.selectedItem$.value;
  // }
  // set selectedItem(node) {
  //   this.selectedItem$.next(node.name);
  // }

  /*
      Getters
      and Setters
  */
  get tree() {
    return this.treeData$.value;
  }
  set tree(newTree) {
    this.treeData$.next(newTree);
    // console.log(`Account Tree has been setup`, this.tree);
    // Bug fix 1798
    // this.applyFix1798();
  }

  /*
      Constructor
      and ngOnInit
  */
  constructor(public balanceSheetService: BalanceSheetService, public menu: MenuService) {
    this.nestedTreeControl = new NestedTreeControl<AccountTreeNode>(this._getChildren);
  }

  ngOnInit() {
    // Auto-update of the tree
    this.treeData$.subscribe(newData => (this.nestedDataSource.data = newData));
    // Auto-update the tree when Balance Sheet Structure changes
    this.onBalanceSheetChange();
  }

  // public isItemSelected = (name: string): boolean => name === this.selectedItem;

  public isOrphan = (node: AccountTreeNode) => node.name.toLowerCase() === this.wordingForOrphans.toLowerCase();

  public isExpandedNode = (node: AccountTreeNode) => this.nestedTreeControl.isExpanded(node);

  public hasNestedChild = (_: number, node: AccountTreeNode) => node.children !== null && node.children.length > 0;

  private _getChildren = (node: AccountTreeNode) => node.children;

  // public expandNode = (node: AccountTreeNode) => this.nestedTreeControl.expand(node);

  // public collapseNode = (node: AccountTreeNode) => this.nestedTreeControl.collapse(node);

  public toggleNode = (node: AccountTreeNode) => this.nestedTreeControl.toggle(node);

  /*
        Listeners / onChange
        related methods
  */

  // Keeps listening to changes in the balance sheet data to update the tree
  private onBalanceSheetChange() {
    // Balance Sheet Data Changes
    this.balanceSheetService.dataChange.subscribe(balanceSheetData => {
      this.bsData = balanceSheetData;
      if (balanceSheetData !== null && balanceSheetData.length > 0) {
        // Build the tree nodes from Balance Sheet Data. The result is a filtered list of
        // `AccountTreeNode` with nested file node as children
        this.setupTree(balanceSheetData);

        // Expand all nodes of the tree by default at the first load
        // if (this.isFirstLoad) {
        //   this.expandAllNodes();
        //   this.isFirstLoad = false;
        // }
      }
    });
  }

  /*
        Setup tree
  */
  /**
   * It receives the current data of the balance sheet and converts it into a tree
   * Extra explanation about this function:
   * --------------------------------------
   *  parent has children
   *  children need to be ordered by displayOrder from account interface
   *      --parent
   *        --child1
   *        --child2
   *          --childA
   *          --childB
   *
   *        The root node in the tree has the following structure
   *          value (number of the account)
   *          name  (name of the account)
   *          subTitle  (again use name of the account)
   *          expanded (set to false by default)
   *          children  (any children, can be a list, that have this account number as their parent account number)
   *
   *          A root node is found where parent account number = -1
   *
   *
   *        Any child node has the following structure
   *          name  (name of the account)
   *          subTitle  (again use name of the account)
   *          expanded (set to false by default)
   *          children  (any children, can be a list, that have this account number as their parent account number)
   *
   *          A child node is found where parent account number != -1
   *
   *         There may be scenarios where an accounts parent does not exist, this happens when the user
   *         is editing the balance sheet and they enter a wrong number
   *         Since we are using the sheet data also, we will need to show this in the tree structure.
   *         For any account where the parent cannot be found, we will group those accounts under a pranet called 'Orphaned'
   */
  private setupTree(balanceSheetData) {
    // Resets
    this.errorsDisplayingTheTree = false;

    // Find the root parents
    // -----------------------
    // -- Accounts with parent === -1
    const rootAccounts = balanceSheetData.filter(account => account['ParentAcctNum'] === -1);

    // -- Orphan Accounts (parent invalidated, not contained in the list of accounts)
    const orphanAccounts = balanceSheetData.filter(account => {
      const parentCondition = parentAccountCandidate =>
        parentAccountCandidate['AccountNumber'] === account['ParentAcctNum'];
      const isOrphan = !balanceSheetData.some(parentCondition) && !rootAccounts.includes(account);
      return isOrphan;
    });

    // Build the tree
    // ---------------
    const sortedAccounts = rootAccounts
      // Bug Fix 3153: Reordered display of the balance sheet elements based on:
      // Display Order first, then Account number, then Mnemonic alphabetically
      .sortBalanceTree()
      .map(account => this.buildAccountTree(balanceSheetData, account))
      // To remove null returned nodes
      .filter(x => x);

    let sortedOrphans = orphanAccounts
      // Bug Fix 3153: Reordered display of the balance sheet elements based on:
      // Display Order first, then Account number, then Mnemonic alphabetically
      .sortBalanceTree()
      .map(orphan => this.buildAccountTree(balanceSheetData, orphan))
      // To remove null returned nodes
      .filter(x => x);

    // For consistency: If there is no children for orphan accounts set children to null
    if (JSON.stringify(sortedOrphans) === JSON.stringify([])) {
      sortedOrphans = null;
    }

    const accountsRootNode = this.buildAccountTreeNode('Accounts', sortedAccounts);
    const orphansRootNode = this.buildAccountTreeNode(this.wordingForOrphans, sortedOrphans);

    const tree = [
      accountsRootNode, // sorted tree of NON-ORPHAN accounts. Accessible throught this.tree[0].children
      orphansRootNode // sorted tree of ORPHAN accounts. Accessible throught this.tree[1].children
    ];

    this.tree = tree;

    this.expandAllNodes();
    return tree;
  }

  // Helper function that creates a Node in the Tree with the right format using the data passed as parameters
  private buildAccountTreeNode(name: string, children: AccountTreeNode[] = null, value: number = -999999) {
    const node = new AccountTreeNode();
    node.name = name;
    node.subTitle = name;
    node.value = value;
    node.children = children;
    return node;
  }

  // Recursively looks for children in each account and appends it, creating a final tree nested structure
  private buildAccountTree(bsData, ancestorAccount) {
    let params;

    if (ancestorAccount['AccountName'] && ancestorAccount['AccountNumber']) {
      params = {
        name: ancestorAccount['AccountName'],
        value: ancestorAccount['AccountNumber']
      };

      // Catch the "Same parent and child account number" ERROR
      try {
        params['children'] = this.getChildrenFromBS(bsData, ancestorAccount['AccountNumber']);
      } catch (err) {
        params['children'] = null;
        this.errorsDisplayingTheTree = true;
      }

      const node = this.buildAccountTreeNode(params.name, params['children'], params.value);
      return node;
    } else {
      this.errorsDisplayingTheTree = true;
      return null;
    }
  }

  // Get children from Balance Sheet Data
  private getChildrenFromBS(bsData, ancestorAccountNumber) {
    const children = bsData
      .filter(
        account =>
          account.ParentAcctNum === ancestorAccountNumber &&
          typeof account['AccountName'] === 'string' &&
          typeof account['Category'] === 'string' &&
          account['Category'] && // Fix 1446
          account['Category'].length > 0
      )
    // Bug Fix 3153: Reordered display of the balance sheet elements based on:
    // Display Order first, then Account number, then Mnemonic alphabetically
      .sortBalanceTree();

    // Stop condition for the recursion: if it has no children, return null
    if (children && children.length === 0) {
      return null;
    }

    // Recursion search of children of each children
    const accountTree = children.map(childAccount => {
      const childNode = new AccountTreeNode();
      childNode.value = childAccount['AccountNumber'];
      childNode.name = childAccount['AccountName'];
      childNode.subTitle = childAccount['AccountName'];
      childNode.children = this.getChildrenFromBS(bsData, childAccount['AccountNumber']);
      return childNode;
    });
    return accountTree;
  }

  /*
        Expand and Collapse
        methods
  */

  /**
   * Expands all the nodes of the tree
   * @params {}
   */
  public expandAllNodes() {
    // console.log('Expanding all nodes of the account tree');
    this.nestedTreeControl.dataNodes = this.tree;
    this.nestedTreeControl.expandAll();
  }

  /**
   * Collapses all the nodes of the tree
   * @params {}
   */
  public collapseAllNodes() {
    // console.log('Collapsing all nodes of the account tree');
    this.nestedTreeControl.dataNodes = this.tree;
    this.nestedDataSource.data.forEach(rootNode => this.nestedTreeControl.collapseDescendants(rootNode));
  }

  // public switchSelected(itemName: string) {
  //   const selectedItem = itemName.toLowerCase();
  //   console.warn('This button does nothing. You have clicked', selectedItem);
  // }

  /*
   **  OTHERS
   */

  /**
   * Bug Fix 1798. Applies the right style to HTML Element depending of the width of other.
   * Could be improved with an observable or other techniques
   */
  // private applyFix1798() {
  //   setTimeout(() => {
  //     const treeHtmlElement = document.getElementsByClassName('account-tree')[0];
  //     const treePaneHtmlElem = document.getElementsByClassName('pane-right')[0];
  //     if (treeHtmlElement) {
  //       treeHtmlElement['style'].minWidth = treePaneHtmlElem.clientWidth + 'px';
  //     }
  //   }, 0);
  // }
}
